/* ==========================================================================
   Ze Feuquingue Store — market-aware free shipping progress bar
   Renders inside the cart drawer and the cart page.

   HOW THE NUMBERS ARE SET
   -----------------------
   Real shipping rules (Shopify > Settings > Shipping > "Profil general"):
     France zone         -> free from  EUR 42.00
     EU zone (28 states) -> free from  EUR 60.00
     International zone  -> free from  EUR 90.00

   Markets use local currencies, and Liquid cannot convert currency, so the
   non-EUR figures below are the EUR threshold converted at early-Aug-2026
   rates and then ROUNDED UP to a clean number. Rounding up is deliberate:
   the customer may unlock free shipping slightly before the bar says so,
   which is a nice surprise. Rounding down would promise free shipping that
   does not exist.

   TO EDIT: change the numbers in THRESHOLDS below. They are in "cents"
   (the amount x 100), because that is how Shopify reports cart totals —
   including for zero-decimal currencies such as JPY.
   Any currency not listed here simply hides the bar rather than showing a
   wrong number.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__fqShipBar) return;
  window.__fqShipBar = true;

  var EU_COUNTRIES = [
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'DE', 'GR', 'HU',
    'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI',
    'ES', 'SE'
  ];

  var THRESHOLDS = {
    EUR_FR: 4200,     // France            EUR 42.00  (exact, no FX risk)
    EUR: 6000,        // rest of the EU    EUR 60.00  (exact, no FX risk)
    GBP: 8000,        // GBP 80     (EUR 90 ~ GBP 77)
    USD: 11000,       // USD 110    (EUR 90 ~ USD 104)
    CAD: 15500,       // CAD 155    (EUR 90 ~ CAD 146)
    AUD: 16000,       // AUD 160    (EUR 90 ~ AUD 147)
    NZD: 17500,       // NZD 175
    CHF: 9000,        // CHF 90     (EUR 90 ~ CHF 84)
    AED: 40000,       // AED 400    (AED is pegged to USD)
    HKD: 86000,       // HKD 860    (HKD is pegged to USD)
    SGD: 14500,       // SGD 145
    JPY: 1650000      // JPY 16,500 (Shopify reports JPY x100 too)
  };

  /* Products that ship on their own profile ("Physical books (Lulu) - no
     free shipping") and therefore NEVER qualify for the free-shipping deal.
     Shopify evaluates a price threshold per shipping profile, so their value
     must not count here either, or the bar promises what checkout won't give. */
  var NO_FREE_SHIP = {
    15963510931787: 1,  /* The Feuquingue Cookbook */
    15963510833483: 1,  /* The Feuquingue Cookbook - Enhanced Graphics */
    15963510800715: 1   /* The Feuquingue Cookbook (hardcover) */
  };

  var TARGETS = ['.cart-drawer__summary', '.cart-summary__inner'];

  var state = { total: 0, currency: null, country: null, ships: false, ready: false };

  /* ---------------------------------------------------------------- utils */

  function country() {
    return (window.Shopify && window.Shopify.country) || state.country || '';
  }

  function thresholdFor(currency) {
    if (!currency) return 0;
    if (currency === 'EUR') {
      return country() === 'FR' ? THRESHOLDS.EUR_FR : THRESHOLDS.EUR;
    }
    return THRESHOLDS[currency] || 0;
  }

  function money(cents, currency) {
    var value = cents / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency,
        maximumFractionDigits: value % 1 === 0 ? 0 : 2
      }).format(value);
    } catch (e) {
      return value.toFixed(2) + ' ' + currency;
    }
  }

  /* --------------------------------------------------------------- render */

  function markup() {
    var threshold = thresholdFor(state.currency);
    if (!state.ready || !threshold || !state.ships || state.total <= 0) return null;

    var remaining = threshold - state.total;
    var won = remaining <= 0;
    var pct = won ? 100 : Math.max(3, Math.round((state.total / threshold) * 100));

    var message = won
      ? 'Free shipping unlocked. Magnifique.'
      : 'Only <b>' + money(remaining, state.currency) + '</b> away from free shipping, chef.';

    /* Books always travel on ze printer's own rate, never free. Say so, so the
       bar never over-promises on a cart that mixes books with everything else. */
    if (state.books) {
      message += ' <span style="opacity:.7;font-weight:400">Ze books ship at printer rate.</span>';
    }

    return (
      '<div class="fq-shipbar__msg">' + message + '</div>' +
      '<div class="fq-shipbar__track"><span class="fq-shipbar__fill" style="width:' + pct + '%"></span></div>'
    );
  }

  function render() {
    var html = markup();

    TARGETS.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (host) {
        var bar = host.querySelector(':scope > .fq-shipbar');

        if (!html) {
          if (bar) bar.remove();
          return;
        }

        if (!bar) {
          bar = document.createElement('div');
          bar.className = 'fq-shipbar';
          host.insertBefore(bar, host.firstChild);
        }

        var won = state.total >= thresholdFor(state.currency);
        bar.classList.toggle('fq-shipbar--won', won);
        if (bar.innerHTML !== html) bar.innerHTML = html;
      });
    });
  }

  /* ----------------------------------------------------------------- data */

  var pending = null;

  function refresh() {
    if (pending) return pending;

    pending = fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + 'cart.js'
      : '/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var lines = cart.items || [];
        var eligible = 0;
        var eligibleShips = false;
        var hasBooks = false;
        for (var li = 0; li < lines.length; li++) {
          var line = lines[li];
          if (NO_FREE_SHIP[line.product_id]) { hasBooks = true; continue; }
          eligible += (line.final_line_price != null ? line.final_line_price : (line.line_price || 0));
          if (line.requires_shipping) eligibleShips = true;
        }

        /* Verified live against /cart/shipping_rates.json: Shopify evaluates a
           price-based rate condition against the WHOLE cart subtotal, not the
           per-profile subtotal. So progress is measured on cart.total_price.
           But the free rate only exists in the general profile, so a cart that
           holds nothing except books can never win it -> eligibleShips is false
           there and the bar hides itself instead of promising a lie. */
        state.total = cart.total_price || 0;
        state.eligible = eligible;
        state.books = hasBooks;
        state.currency = cart.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active);
        state.ships = eligibleShips && eligible > 0;
        state.ready = true;
        render();
      })
      .catch(function () { /* stay quiet — never break the cart */ })
      .then(function () { pending = null; });

    return pending;
  }

  /* -------------------------------------------------------------- signals */

  // 1. Any cart mutation that goes through fetch.
  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var result = nativeFetch.apply(this, arguments);

      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        result.then(function () { setTimeout(refresh, 60); }).catch(function () {});
      }
      return result;
    };
  }

  // 2. Theme events (Horizon dispatches its own, names vary by version).
  ['cart:update', 'cart:refresh', 'CartUpdateEvent', 'cart:change'].forEach(function (name) {
    document.addEventListener(name, function () { setTimeout(refresh, 60); });
  });

  // 3. Section re-renders swap the summary node out from under us.
  var observer = new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.classList && (node.classList.contains('fq-shipbar') ||
            node.parentElement && node.parentElement.classList.contains('fq-shipbar'))) continue;

        for (var k = 0; k < TARGETS.length; k++) {
          if (node.matches && node.matches(TARGETS[k])) { render(); return; }
          if (node.querySelector && node.querySelector(TARGETS[k])) { render(); return; }
        }
      }
    }
  });

  function start() {
    observer.observe(document.body, { childList: true, subtree: true });
    refresh();

    // The cart drawer lives in a <dialog> that the theme injects and replaces
    // at unpredictable moments, so the observer alone can miss it. render() is
    // idempotent and costs two querySelectorAll calls, so a slow heartbeat is
    // the cheapest way to guarantee the bar is wherever the summary ends up.
    setInterval(render, 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
